import {Avatar, Modal} from 'antd'
import {getId} from 'apis/model/base'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useMemo, useState} from 'react'
import {NullAsDefault, renderElse, renderIf} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {SlateRenderer} from 'views/SlateEditor'
import {convertFromString, isEmptyContent} from 'views/SlateEditor/functions'
import {renderCourseSubject} from 'views/Workspace/Course/functions/renderCourseSubject'
import {ItemInformation} from "../../Organization/OrganizationEntity";
import {HeaderWithIconLine} from "../../Templates/items/ListHeader";
import {RiSubscript} from "react-icons/ri";
import {GoDotFill} from "react-icons/go";
import Translate from "../../../../modules/local/Translate";
import {TbListDetails} from "react-icons/tb";
import {BsInfoCircleFill} from "react-icons/bs";
import LoadingBlock from "../../../LoadingBlock";

const renderSubjectItem = ({renderActions = NullAsDefault, handleAction = NullAsDefault} = {}) => (item, index) => {
    const {subject} = item ?? {}
    const {description} = subject ?? {}

    return (
        <Translate key={index}>
            {(t) => (
                <div className="p-2 flex flex-col hover:shadow-md rounded-xl relative">
                    <div className="font-bold text-sm text-color-000 hover:text-primary leading-snug cursor-pointer">
                        {`${index + 1}. ${getTitle(subject)}`}
                    </div>
                    {renderIf(
                        description,
                        <div className="ml-3 mb-1 text-xs text-color-300 tracking-wider max-lines-2">
                            {description}
                        </div>
                    )}
                    <div className="ml-3 flex items-center gap-1">
                        <GoDotFill size={10} className="text-color-400"/>
                        <span className="text-xs text-color-500 italic">
                            {`${t('number of sessions')} : `}
                        </span>
                        <span className="font-medium text-xs text-blue-600 italic">
                            {`${item?.number_of_sessions} ${t('sessions')}`}
                        </span>
                        <div className="flex-1"/>
                        {renderActions(item)}
                    </div>
                </div>
            )}
        </Translate>
    )
}

const DetailModalContent = ({item}) => {
    const t = useTranslate()
    const {
        subject,
        number_of_sessions, about,
        passed_attendance_percentage,
        passed_score,
    } = item ?? {}

    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
                <Avatar
                    size={50}
                    icon={<RiSubscript size={26} className="text-primary-500"/>}
                    className="flex flex-center bg-primary-50 rounded-lg 111-111"
                />
                <div className="flex flex-col">
                    <span className="font-bold text-lg text-color-000">
                        {getTitle(subject)}
                    </span>
                    <ItemInformation label="subject code" value={`#${subject?.subject_idcode}`} className="text-xs italic"/>
                </div>
            </div>
            {subject?.description && (
                <div className="ml-2 text-color-300 tracking-wider">
                    {subject?.description}
                </div>
            )}
            <div className="flex flex-col gap-2">
                <HeaderWithIconLine
                    icon={<BsInfoCircleFill size={14} className="text-blue-400 mr-1"/>}
                    title="subject information"
                    className="text-sm text-color-400"
                />
                <div className="ml-0 md:ml-1 flex flex-col flex-1 gap-1">
                    <ItemInformation
                        showIcon={true}
                        label="number of sessions"
                        value={`${number_of_sessions} ${t('sessions')}`}
                        className="text-xs italic"
                    />
                    {renderIf(
                        passed_score,
                        <ItemInformation
                            showIcon={true}
                            label="passed score"
                            value={passed_score}
                            className="text-xs italic"
                        />
                    )}
                    {renderIf(
                        passed_attendance_percentage,
                        <ItemInformation
                            showIcon={true}
                            label="passed attendance percentage"
                            value={`${passed_attendance_percentage} ${t('%')}`}
                            className="text-xs italic"
                        />
                    )}
                    {renderElse(
                        isEmptyContent(about),
                        <div className="flex flex-col">
                            <div className="space-x-1">
                                <GoDotFill size={10} className="text-color-400"/>
                                <span className="text-sm text-color-500 italic">
                              {`${t('introduce')} : `}
                          </span>
                            </div>
                            <SlateRenderer value={convertFromString(about)}/>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const CourseClassSubjects = ({
                                 item,
                                 renderList,
                                 detail = false,
                                 ...props
                             }) => {
    const t = useTranslate()

    const {edit} = item ?? {}

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const data = _.get(item, 'class_subjects', [])
    const groupedData = _.groupBy(data, 'group_name');
    const groupedDataKeysNotSorted = Object.keys(groupedData);
    // Lọc ra các phần tử null
    const nullElements = _.filter(groupedDataKeysNotSorted, element => element === null);
    // Lọc ra các phần tử không phải null
    const nonNullElements = _.filter(groupedDataKeysNotSorted, element => element !== null);
    // Sắp xếp các phần tử không phải null
    const sortedNonNullElements = _.sortBy(nonNullElements, [element => element.toString().toLowerCase()]);
    // Gộp các phần tử null với các phần tử đã sắp xếp
    const groupedDataKeys = [...nullElements, ...sortedNonNullElements];

    const onCancel = () => {
        setAction({})
    }

    const modal = useMemo(() => {
        const {subject} = action?.value ?? {}
        const id = getId(subject)
        let Content = NullAsDefault
        let Wrapper = ({children}) => children({item: {}})
        let params = {}
        let modal_params = {}

        switch (action?.type) {
            case CRUD.READ:
                modal_params = {
                    width: 600,
                    footer: undefined,
                    cancelText: t('close'),
                    okButtonProps: {
                        hidden: true,
                    },
                    cancelButtonProps: {
                        className: 'text-color-200 button-rounded-lg no-border',
                    },
                    title: (
                        <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                            {t('subjects')}
                        </div>
                    ),
                }
                params = {item: action?.value}
                Content = DetailModalContent
                // Wrapper = withProps({id})(FetchSubjectById)
                break
            default:
                break
        }

        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                destroyOnHidden={true}
                open={action?.type}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({item, isLoading}) => {
                        if (!!isLoading) {
                            return <LoadingBlock/>
                        }
                        return (
                            <Content
                                {...params}
                                {...{item: action?.value, onCancel}}
                            />
                        )
                    }}
                </Wrapper>
            </Modal>
        )
    }, [item, action])

    const renderActions = useCallback(
        (item) => {
            return (
                <div
                    onClick={() => {
                        setAction({type: CRUD.READ, value: item})
                    }}
                    className="text-xs text-color-400 hover:text-color-100 italic cursor-pointer">
                    {`${t('view detail')}...`}
                </div>
            )
        },
        []
    )

    return (
        <React.Fragment>
            {_.isFunction(renderList) ? (
                renderList({
                    edit,
                    action,
                    detail,
                    renderItem: renderCourseSubject({
                        detail,
                        renderActions,
                    }),
                })
            ) : (
                <div className="flex flex-col items-start space-y-6">
                    <div className="w-full flex flex-col flex-1 gap-3">
                        <HeaderWithIconLine
                            icon={<RiSubscript size={18} className="text-color-500"/>}
                            title="list of subjects"
                            className="text-lg text-color-400"
                        />
                        <div style={{marginLeft: 10}} className="flex flex-col gap-4">
                            {groupedDataKeys.map((e, i) => {
                                return (
                                    <div key={i} className="w-full flex flex-col flex-1 gap-3">
                                        {renderIf(
                                            e !== 'null' && groupedDataKeys.length > 1,
                                            <div
                                                style={{
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    background: 'linear-gradient(to right, var(--background-200), rgba(0, 0, 0, 0) 100%)'
                                                }}
                                                className="w-90 flex items-center gap-1">
                                                <TbListDetails className="text-color-300"/>
                                                <span className="font-semibold text-sm text-color-300 uppercase tracking-wider">
                                                    {`  -  ${e}`}
                                                </span>
                                            </div>
                                        )}
                                        <div style={{marginLeft: 4}} className="space-y-4">
                                            {_.sortBy(_.get(groupedData, e, []), ['subject.title']).map((ee, ii) => {
                                                return renderSubjectItem({
                                                    renderActions,
                                                    handleAction: (type, value) => {
                                                        setAction({type, value});
                                                    },
                                                })(ee, ii)
                                            })}
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}
            {modal}
        </React.Fragment>
    )
}

export default CourseClassSubjects
